import { ChangeDetectionStrategy, Component, Inject } from "@angular/core";
import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { FeaturePermissionName } from "@common/ADAPT.Common.Model/embed/feature-permission-name.enum";
import { Meeting } from "@common/ADAPT.Common.Model/organisation/meeting";
import { MeetingAgendaTemplate } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-template";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { ORGANISATION_CONFIGURATION_PAGE, TEAM_CONFIGURATION_PAGE } from "@common/page-route-providers";
import { IAdaptRoute } from "@common/route/page-route-builder";
import { ADAPT_DIALOG_DATA } from "@common/ux/adapt-common-dialog/adapt-common-dialog.globals";
import { BaseDialogComponent } from "@common/ux/adapt-common-dialog/base-dialog.component/base-dialog.component";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { ConfigArea } from "@org-common/lib/configuration/configuration-types";
import { FeaturesService } from "@org-common/lib/features/features.service";
import { CommonTeamsAuthService } from "@org-common/lib/teams/common-teams-auth.service";
import { catchError, map, switchMap, tap } from "rxjs";
import { MeetingsService } from "../meetings.service";

@Component({
    selector: "adapt-new-meeting-template-dialog",
    standalone: false,
    templateUrl: "./new-meeting-template-dialog.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./new-meeting-template-dialog.component.css",
})
export class NewMeetingTemplateDialogComponent extends BaseDialogComponent<Meeting> {
    public readonly dialogName = "NewMeetingTemplateDialog";

    public templateName: string;
    public templateLocationTeamId: number | null; // null for organisation template

    public constructor(
        @Inject(ADAPT_DIALOG_DATA) public meeting: Meeting,
        @Inject(TEAM_CONFIGURATION_PAGE) private teamConfigurationPageRoute: IAdaptRoute<{ teamId?: number }>,
        @Inject(ORGANISATION_CONFIGURATION_PAGE) private organisationConfigurationPageRoute: IAdaptRoute<any>,
        private meetingsService: MeetingsService,
        private authService: AuthorisationService,
        private teamsAuthService: CommonTeamsAuthService,
        private featuresService: FeaturesService,
    ) {
        super();

        this.templateName = this.meeting.name;
        this.templateLocationTeamId = this.meeting.teamId;
    }

    @Autobind
    public filter(team: Team | null) {
        if (!team) {
            return this.authService.currentPersonHasPermission(FeaturePermissionName.OrganisationConfigure);
        } else {
            return this.featuresService.isFeatureActive(FeatureName.StewardshipWorkMeetings, team) && this.teamsAuthService.currentPersonCanEditTeam(team);
        }
    }


    public onTeamsSelectionChanged(selectedTeams: (Team | null)[]) {
        // only expected selectedTeams to be one team as single selection is enforced
        if (selectedTeams.length > 0) {
            this.templateLocationTeamId = selectedTeams[0]?.teamId ?? null;
        }
    }

    @Autobind
    public saveAsMeetingTemplate() {
        return this.meetingsService.createAgendaTemplate(this.templateLocationTeamId ?? undefined).pipe(
            switchMap((template) => {
                template.name = this.templateName;
                template.meetingDescription = this.meeting.supplementaryData?.purpose;
                template.version = 1;
                // need to save first to import agenda items into the template
                return this.meetingsService.saveEntities(template);
            }),
            switchMap((savedTemplate: MeetingAgendaTemplate) =>
                this.meetingsService.importAgendaItems(this.meeting.meetingAgendaItems, undefined, savedTemplate.meetingAgendaTemplateId).pipe(
                    switchMap(() => this.meetingsService.saveEntities()), // save all as items may have supp data should is not tracked by this pipe
                    map(() => savedTemplate),
                )),
            tap((savedTemplate) => {
                this.resolve(this.meeting);
                setTimeout(() => {
                    // redirect to the template page after the dialog is closed next digest (this save is still within the pipe of dialog save)
                    const queryParams = { section: ConfigArea[ConfigArea.Meetings], editTemplateId: savedTemplate.meetingAgendaTemplateId };
                    if (this.templateLocationTeamId) {
                        this.teamConfigurationPageRoute.gotoRoute({ teamId: this.templateLocationTeamId ?? undefined }, queryParams)
                            .subscribe();
                    } else {
                        this.organisationConfigurationPageRoute.gotoRoute(undefined, queryParams)
                            .subscribe();
                    }
                });
            }),
            catchError((error) => {
                this.setErrorMessage(ErrorHandlingUtilities.getHttpResponseMessage(error));
                throw error;
            }),
        );
    }
}
